import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { findBySlug, relatedTo } from "@/lib/inventory";
import { useTitle } from "@/lib/site";
import { Breadcrumbs, SectionHeading } from "@/components/site/Shared";
import { Reveal } from "@/components/motion/Reveal";
import { Gallery } from "@/components/vehicles/Gallery";
import { VehicleSummary } from "@/components/vehicles/VehicleSummary";
import { VehicleSections } from "@/components/vehicles/VehicleSections";
import { MobileActionBar } from "@/components/vehicles/MobileActionBar";
import { VehicleCard } from "@/components/vehicles/VehicleCard";

const Missing = () => (
  <section className="container-x py-24 text-center" data-testid="vehicle-not-found">
    <h1 className="font-display text-4xl font-extrabold">This car isn't in our stock</h1>
    <p className="mx-auto mt-4 max-w-md text-steel">It may have been sold or the link may be out of date.</p>
    <Link to="/used-cars" className="btn btn-primary mt-8" data-testid="vehicle-not-found-stock-button">
      <ArrowLeft aria-hidden="true" /> Browse all cars
    </Link>
  </section>
);

export default function VehicleDetail() {
  const { slug } = useParams();
  const v = findBySlug(slug);
  useTitle(v ? `${v.year} ${v.make} ${v.model} ${v.variant} | YK Motors` : "Car not found | YK Motors");
  if (!v) return <Missing />;
  const related = relatedTo(v);

  return (
    <div className="pb-24 lg:pb-0" data-testid="vehicle-detail-page">
      <div className="container-x pt-6 lg:pt-10">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Used cars", to: "/used-cars" }, { label: `${v.make} ${v.model}` }]} />
      </div>
      <div className="container-x mt-6 grid gap-x-12 lg:grid-cols-12">
        <div className="min-w-0 lg:col-span-7 lg:row-start-1 xl:col-span-8">
          <Gallery v={v} />
        </div>
        <aside className="mt-8 lg:col-span-5 lg:col-start-8 lg:row-span-2 lg:row-start-1 lg:mt-0 xl:col-span-4 xl:col-start-9" aria-label="Price and enquiry">
          <div className="lg:sticky lg:top-28">
            <VehicleSummary v={v} />
          </div>
        </aside>
        <div className="min-w-0 lg:col-span-7 lg:row-start-2 xl:col-span-8">
          <VehicleSections v={v} />
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-20 bg-paper py-16 lg:mt-28 lg:py-24" aria-labelledby="related-heading" data-testid="related-vehicles-section">
          <div className="container-x">
            <SectionHeading id="related-heading" eyebrow="Related vehicles" title="You might also like." />
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r, i) => (
                <Reveal key={r.id} delay={i * 0.08}>
                  <VehicleCard v={r} testId={`related-card-${i}`} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
      <MobileActionBar v={v} />
    </div>
  );
}
